import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { normalizePhone, formatPhoneDisplay } from '../utils/phoneFormat';
import Toast from '../components/ui/Toast';
import Swal from 'sweetalert2';
import PrivacyModal from '../components/ui/PrivacyModal';
import './LoginPage.css';

const staggerContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1,
    }
  }
};

const itemReveal = {
  hidden: { opacity: 0, y: 20 },
  show: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
  }
};

const LoginPage = () => {
  const { member, loginByPhone, loginByEmail, registerMember, verifyWithLine, loading } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState('');
  const [fullName, setFullName] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [isDev, setIsDev] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [secretKey, setSecretKey] = useState('');
  const [acceptPDPA, setAcceptPDPA] = useState(false);
  const [showPDPA, setShowPDPA] = useState(false);

  // If already logged in, redirect to dashboard
  if (member) {
    return <Navigate to="/" replace />;
  }

  const handlePhoneChange = (e) => {
    // Only allow digits and max 10 chars for phone
    const val = e.target.value.replace(/\D/g, '').substring(0, 10);
    setPhone(val);
    
    // Clear secret key if they change the phone from 8888
    if (val !== '8888') {
      setSecretKey('');
    }
    setErrorMsg('');
  };

  const handleMouseMove = (e) => {
    // Only run on desktop/devices with hover
    if (window.innerWidth < 1024) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Glow position
    e.currentTarget.style.setProperty('--mouse-x', `${x}px`);
    e.currentTarget.style.setProperty('--mouse-y', `${y}px`);
    
    // 3D Tilt calculation (max 5 degrees)
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = ((y - centerY) / centerY) * -4;
    const rotateY = ((x - centerX) / centerX) * 4;
    
    e.currentTarget.style.setProperty('--rotate-x', `${rotateX}deg`);
    e.currentTarget.style.setProperty('--rotate-y', `${rotateY}deg`);
  };

  const handleMouseLeave = (e) => {
    // Snap back to 0
    e.currentTarget.style.setProperty('--rotate-x', `0deg`);
    e.currentTarget.style.setProperty('--rotate-y', `0deg`);
  };

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    if (!acceptPDPA) {
      setErrorMsg('กรุณาอ่านและยอมรับนโยบายความเป็นส่วนตัวก่อนเข้าสู่ระบบ');
      return;
    }
    if (phone.length < 10) {
      setErrorMsg('กรุณากรอกเบอร์โทรศัพท์ 10 หลัก');
      return;
    }

    try {
      // 1. Try to verify with LINE first
      const lineProfile = await verifyWithLine();
      
      // 2. Try to login
      const foundMember = await loginByPhone(phone);
      
      if (foundMember) {
        Swal.fire({
          title: 'เข้าสู่ระบบสำเร็จ',
          text: `ยินดีต้อนรับคุณ ${foundMember.full_name}`,
          icon: 'success',
          timer: 1500,
          showConfirmButton: false
        }).then(() => {
          navigate('/');
        });
      } else {
        setStep(2);
      }
    } catch (err) {
      setErrorMsg('');
      Swal.fire({
        title: 'เกิดข้อผิดพลาด',
        text: err.message || 'เกิดข้อผิดพลาดในการเข้าสู่ระบบ',
        icon: 'error',
        confirmButtonColor: '#ef4444'
      });
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    if (!fullName.trim()) {
      setErrorMsg('กรุณากรอกชื่อ-นามสกุล');
      return;
    }

    try {
      await registerMember(fullName, phone);
      Swal.fire({
        title: 'ลงทะเบียนสำเร็จ',
        text: 'ยินดีต้อนรับสู่ระบบสมาชิกของเรา',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      }).then(() => {
        navigate('/');
      });
    } catch (err) {
      setErrorMsg('');
      Swal.fire({
        title: 'เกิดข้อผิดพลาด',
        text: err.message || 'เกิดข้อผิดพลาดในการลงทะเบียน',
        icon: 'error',
        confirmButtonColor: '#ef4444'
      });
    }
  };

  const handleDevLogin = async (e) => {
    e.preventDefault();
    try {
      await loginByEmail(email, password);
      Swal.fire({
        title: 'Dev Login สำเร็จ',
        icon: 'success',
        timer: 1000,
        showConfirmButton: false
      }).then(() => {
        navigate('/');
      });
    } catch (err) {
      setErrorMsg('');
      Swal.fire({
        title: 'เข้าสู่ระบบล้มเหลว',
        text: err.message,
        icon: 'error',
        confirmButtonColor: '#ef4444'
      });
    }
  };

  const toggleDevMode = () => {
    setIsDev(!isDev);
    setErrorMsg('');
  };

  return (
    <motion.div 
      className="login-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
    >
      {/* Desktop Left Panel */}
      <div 
        className="login-left-panel"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <div className="login-left-bg"></div>
        <div className="login-left-glow"></div>
        <motion.div
          className="panel-content"
          variants={staggerContainer}
          initial="hidden"
          animate="show"
        >
          <motion.h1 className="panel-title" variants={itemReveal}>
            Clean Food<br/>CR<span className="title-dot">.</span>
          </motion.h1>
          <motion.p className="panel-subtitle" variants={itemReveal}>
            ยกระดับสุขภาพชาวเชียงราย<br/>ผ่านมื้ออาหารที่ใส่ใจ
          </motion.p>
          
          <motion.div className="panel-badge" variants={itemReveal}>
            EST. 2026
          </motion.div>
        </motion.div>
      </div>

      {/* Right Form Panel */}
      <motion.div 
        className="login-right-panel"
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="login-container">
          <motion.div 
            className="login-header"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            <h1 className="login-title">Clean Food CR</h1>
            <p className="login-subtitle">ระบบสมาชิกพอร์ทัล</p>
          </motion.div>

          <AnimatePresence mode="wait">
            {!isDev ? (
              step === 1 ? (
                <motion.form 
                  key="step1"
                  onSubmit={handlePhoneSubmit}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  className="login-form"
                >
                  <div className="form-group">
                    <label>เบอร์โทรศัพท์มือถือ</label>
                    <input 
                      type="text" 
                      inputMode="tel"
                      value={phone}
                      onChange={handlePhoneChange}
                      placeholder="08X-XXX-XXXX"
                      autoFocus
                      disabled={loading}
                      className={errorMsg ? 'has-error' : ''}
                    />
                    {errorMsg && <span className="error-text">{errorMsg}</span>}
                  </div>
                  
                  <AnimatePresence>
                    {phone === '8888' && (
                      <motion.div
                        initial={{ opacity: 0, height: 0, marginTop: 0 }}
                        animate={{ opacity: 1, height: 'auto', marginTop: 12 }}
                        exit={{ opacity: 0, height: 0, marginTop: 0 }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        className="secret-form-group"
                      >
                        <input 
                          type="password"
                          value={secretKey}
                          onChange={(e) => {
                            const val = e.target.value;
                            setSecretKey(val);
                            if (val === 'superadmin!!') {
                              setTimeout(() => {
                                setIsDev(true);
                                setPhone('');
                                setSecretKey('');
                              }, 300); // Small delay for UX
                            }
                          }}
                          placeholder="Passphrase"
                          className="secret-input"
                          autoFocus
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="pdpa-checkbox-group">
                    <label className="pdpa-label">
                      <input 
                        type="checkbox" 
                        checked={acceptPDPA}
                        onChange={(e) => {
                          setAcceptPDPA(e.target.checked);
                          setErrorMsg('');
                        }}
                      />
                      <span>ฉันได้อ่านและยอมรับ <button type="button" className="link-btn" onClick={() => setShowPDPA(true)}>นโยบายความเป็นส่วนตัว</button></span>
                    </label>
                  </div>

                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading || (phone !== '8888' && phone.length < 10)}
                  >
                    {loading ? 'กำลังตรวจสอบ...' : 'เข้าสู่ระบบ'}
                  </button>
                </motion.form>
              ) : (
                <motion.form 
                  key="step2"
                  onSubmit={handleRegisterSubmit}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  className="login-form"
                >
                  <div className="alert-info">
                    ไม่พบเบอร์ {formatPhoneDisplay(phone)} ในระบบ กรุณาลงทะเบียนสมาชิกใหม่
                  </div>
                  <div className="form-group">
                    <label>ชื่อ-นามสกุล</label>
                    <input 
                      type="text" 
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="กรอกชื่อ-นามสกุล"
                      autoFocus
                      disabled={loading}
                    />
                  </div>
                  {errorMsg && <span className="error-text">{errorMsg}</span>}
                  <div className="btn-group">
                    <button 
                      type="button" 
                      className="btn btn-outline"
                      onClick={() => setStep(1)}
                      disabled={loading}
                    >
                      กลับ
                    </button>
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                      disabled={loading || !fullName.trim()}
                    >
                      {loading ? 'กำลังลงทะเบียน...' : 'ลงทะเบียน'}
                    </button>
                  </div>
                </motion.form>
              )
            ) : (
              <motion.form 
                key="dev"
                onSubmit={handleDevLogin}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="login-form dev-form"
              >
                <div className="dev-header">
                  <div className="dev-badge">Development Mode</div>
                  <button type="button" className="dev-back-btn" onClick={toggleDevMode}>
                    กลับไปหน้าปกติ
                  </button>
                </div>
                
                <div className="form-group">
                  <label>Email</label>
                  <input 
                    type="email" 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@example.com"
                  />
                </div>
                <div className="form-group">
                  <label>Password</label>
                  <div className="password-input-wrapper">
                    <input 
                      type={showPassword ? "text" : "password"} 
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                    />
                    <button 
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex="-1"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>
                {errorMsg && <span className="error-text">{errorMsg}</span>}
                <button 
                  type="submit" 
                  className="btn btn-primary dev-btn"
                  disabled={loading}
                >
                  {loading ? 'Logging in...' : 'เข้าสู่ระบบ Dev'}
                </button>
              </motion.form>
            )}
          </AnimatePresence>
          
          <motion.div 
            className="login-footer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <p>Clean Food CR Portal v1.0</p>
          </motion.div>
        </div>
      </motion.div>

      <PrivacyModal 
        isOpen={showPDPA} 
        onClose={() => setShowPDPA(false)} 
      />
    </motion.div>
  );
};

export default LoginPage;
